import type { AjusteImagen, BrandPalette } from '../utils/diseno/types';

export interface ImagenRedes {
  id: string;
  empresaId: string;
  nombre: string;
  dataUrl: string;
  createdAt: string;
}

export interface DisenoRedes {
  id: string;
  empresaId: string;
  plantillaId: string;
  formato: string;
  nombre: string;
  slots: Record<string, string>;
  ajustes: Record<string, AjusteImagen>;
  palette: BrandPalette | null;
  usaMarca: boolean;
  caption: string;
  altText: string;
  estado: 'borrador' | 'descargada';
  updatedAt: string;
}

const DB = 'clientelo-tus-redes';

function nid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('imagenes')) db.createObjectStore('imagenes', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('disenos')) db.createObjectStore('disenos', { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function getAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve((req.result || []) as T[]);
    req.onerror = () => reject(req.error);
  });
}

function put(db: IDBDatabase, store: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

const memoria = {
  imagenes: [] as ImagenRedes[],
  disenos: [] as DisenoRedes[],
};

async function leer(empresaId: string): Promise<{ imagenes: ImagenRedes[]; disenos: DisenoRedes[] }> {
  try {
    const db = await openDb();
    if (!db) {
      return {
        imagenes: memoria.imagenes.filter((i) => i.empresaId === empresaId),
        disenos: memoria.disenos.filter((d) => d.empresaId === empresaId),
      };
    }
    const [imagenes, disenos] = await Promise.all([
      getAll<ImagenRedes>(db, 'imagenes'),
      getAll<DisenoRedes>(db, 'disenos'),
    ]);
    return {
      imagenes: imagenes.filter((i) => i.empresaId === empresaId),
      disenos: disenos.filter((d) => d.empresaId === empresaId),
    };
  } catch {
    return {
      imagenes: memoria.imagenes.filter((i) => i.empresaId === empresaId),
      disenos: memoria.disenos.filter((d) => d.empresaId === empresaId),
    };
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function suscribirTusRedes(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function avisar() {
  listeners.forEach((fn) => fn());
}

let cache: { empresaId: string; imagenes: ImagenRedes[]; disenos: DisenoRedes[] } = {
  empresaId: '',
  imagenes: [],
  disenos: [],
};

export function estadoTusRedes(): typeof cache {
  return cache;
}

export async function cargarTusRedes(empresaId: string): Promise<void> {
  const data = await leer(empresaId);
  cache = { empresaId, ...data };
  avisar();
}

export async function agregarImagenRedes(empresaId: string, file: Blob, nombre = 'Imagen del estudio'): Promise<void> {
  const row: ImagenRedes = {
    id: nid(),
    empresaId,
    nombre,
    dataUrl: await fileToDataUrl(file),
    createdAt: new Date().toISOString(),
  };
  memoria.imagenes = [row, ...memoria.imagenes].slice(0, 40);
  try {
    const db = await openDb();
    if (db) await put(db, 'imagenes', row);
  } catch {
    /* memoria */
  }
  if (cache.empresaId === empresaId) {
    cache = { ...cache, imagenes: [row, ...cache.imagenes].slice(0, 40) };
    avisar();
  }
}

export async function guardarDisenoRedes(diseno: DisenoRedes): Promise<void> {
  memoria.disenos = [diseno, ...memoria.disenos.filter((d) => d.id !== diseno.id)];
  try {
    const db = await openDb();
    if (db) await put(db, 'disenos', diseno);
  } catch {
    /* memoria */
  }
  if (cache.empresaId === diseno.empresaId) {
    cache = {
      ...cache,
      disenos: [diseno, ...cache.disenos.filter((d) => d.id !== diseno.id)],
    };
    avisar();
  }
}

export function nuevoIdRedes(): string {
  return nid();
}
